import { createContext, useContext, type ReactNode } from 'react';
import { Image } from 'react-native';

import type { OralExamAggregateTimeSlotStateResponse } from '@/common/api/models/oralExamAggregateTimeSlotStateResponse';
import type { OralExamBookingRunSummary } from '@/common/api/models/oralExamBookingRunSummary';
import type { GetOralExamIdPhotoUrlKind } from '@/common/api/models/getOralExamIdPhotoUrlKind';

/**
 * Visual Lab / commissioning overrides. Production mounts never provide this
 * context — hooks and tiles fall through to LiveKit and the real API.
 */
export type OralExamLabImageSource = string | number | { uri?: string };

export type OralExamLabTileMedia = {
  standInCameraUrl: OralExamLabImageSource;
  standInScreenUrl?: OralExamLabImageSource;
  hasMic?: boolean;
  studentName?: string;
};

export type OralExamLabCohortState = {
  cohort: OralExamAggregateTimeSlotStateResponse | null;
  bookingRuns: OralExamBookingRunSummary[];
  capacity: number;
  isLoading: boolean;
  etag: string | null;
  queryKey: unknown[];
};

export type OralExamLabTileLookup = Pick<OralExamBookingRunSummary, 'bookingId' | 'studentId'>;

export type OralExamLabOverridesValue = {
  cohortState?: OralExamLabCohortState;
  observeBookingId?: string | null;
  resolveTileMedia?: (summary: OralExamLabTileLookup) => OralExamLabTileMedia | null;
  resolveIdPhotoUrl?: (
    bookingId: string,
    kind: GetOralExamIdPhotoUrlKind,
  ) => string | null;
  supervisorStillImageUrl?: OralExamLabImageSource | null;
  supervisorLiveImageUrl?: OralExamLabImageSource | null;
  selfViewImageUrl?: OralExamLabImageSource | null;
  examinerPresent?: boolean;
  /** Pre-flight lab: open the retake dialog for this booking on mount. */
  preflightOpenRetakeBookingId?: string | null;
  /** Pre-flight lab: reject fails locally without calling the API (no 401 logout). */
  preflightSimulateRejectError?: boolean;
  /** Pre-flight lab: optional colleague tile for mixed-state layout commissioning. */
  preflightColleagueStandIn?: {
    identity: string;
    name: string;
    imageUrl: OralExamLabImageSource;
  } | null;
};

const OralExamLabOverridesContext = createContext<OralExamLabOverridesValue | null>(null);

export function OralExamLabOverridesProvider({
  value,
  children,
}: {
  value: OralExamLabOverridesValue;
  children: ReactNode;
}) {
  return (
    <OralExamLabOverridesContext.Provider value={value}>
      {children}
    </OralExamLabOverridesContext.Provider>
  );
}

export function useOralExamLabOverrides(): OralExamLabOverridesValue | null {
  return useContext(OralExamLabOverridesContext);
}

export function standInImageSource(source: OralExamLabImageSource) {
  if (typeof source === 'number') return source;
  if (typeof source === 'string') return { uri: source };
  return source;
}

const STAND_IN_FILL = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  width: '100%' as const,
  height: '100%' as const,
};

/** Cover-fill portrait used when Visual Lab replaces a LiveKit track. */
export function LabStandInImage({
  source,
  testID,
  resizeMode = 'cover',
}: {
  source: OralExamLabImageSource;
  testID?: string;
  resizeMode?: 'cover' | 'contain';
}) {
  return (
    <Image
      source={standInImageSource(source)}
      style={STAND_IN_FILL}
      resizeMode={resizeMode}
      testID={testID}
      accessibilityIgnoresInvertColors
    />
  );
}
